import React, { useEffect, useState } from 'react';
import { XMarkIcon, DocumentTextIcon, ArrowDownTrayIcon, TruckIcon, DocumentDuplicateIcon, CheckIcon } from '@heroicons/react/24/outline';
import type { XmlTerceiro } from '../../types/xmlTerceiros';
import { useBaixarDanfe, useBaixarXml } from '../../hooks/useXmlTerceiros';
import { money, formatCnpj, formatDateTime, statusNfeErp, type StatusNfeErpTone } from '../../utils/format';
import { btnPrimario, btnSecundario } from '../ui/iris';

const TONE_PILL: Record<StatusNfeErpTone, string> = {
  ok: 'bg-ok-soft text-ok',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  neutral: 'bg-surface-2 text-tx-3',
};

interface XmlTerceirosModalProps {
  xml: XmlTerceiro;
  isOpen: boolean;
  onClose: () => void;
}

const XmlTerceirosModal: React.FC<XmlTerceirosModalProps> = ({ xml, isOpen, onClose }) => {
  const baixarDanfeMutation = useBaixarDanfe();
  const baixarXmlMutation = useBaixarXml();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Fechar com ESC + travar scroll do body enquanto aberto.
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onEsc);
      document.body.style.overflow = original;
    };
  }, [isOpen, onClose]);

  const handleCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch { /* silencioso */ }
  };

  const handleBaixarDanfe = () => baixarDanfeMutation.mutate(xml.gid);
  const handleBaixarXml = () => baixarXmlMutation.mutate(xml.gid);

  if (!isOpen) return null;

  const cnpjFormatado = formatCnpj(xml.emitenteCnpj);
  const temFantasia = xml.emitenteFantasia && xml.emitenteFantasia !== xml.emitenteNome;

  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-[var(--scrim)] backdrop-blur-[2px]" onClick={onClose} />

      {/* Mobile: full-screen. sm+: card centralizado. */}
      <div className="fixed inset-0 flex flex-col sm:items-center sm:justify-center sm:p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Detalhes da NF-e"
          className="relative flex h-full w-full flex-col overflow-hidden bg-surface sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-iris-lg sm:border sm:border-line-strong sm:shadow-iris-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-iris bg-accent">
                <DocumentTextIcon className="h-4 w-4 text-white" />
              </span>
              <div className="min-w-0">
                <h2 className="truncate text-base font-semibold text-tx">NF-e {xml.numero}</h2>
                <p className="truncate text-[11px] text-tx-3">{formatDateTime(xml.dataHoraEmissao)}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-iris text-tx-3 transition-colors hover:bg-surface-2 hover:text-tx"
              aria-label="Fechar"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
            {/* Hero — valor num relance + estado */}
            <div className="flex items-end justify-between gap-3 rounded-iris border border-line bg-surface-2 px-4 py-3">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-tx-3">Valor total</p>
                <p className="text-2xl font-bold tabular-nums text-ok">{money(xml.valorTotalNf)}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${TONE_PILL[statusNfeErp(xml.erpStatus).tone]}`}>
                  {statusNfeErp(xml.erpStatus).label}
                </span>
                {xml.emTransito && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-soft px-2.5 py-1 text-xs font-semibold text-accent-text">
                    <TruckIcon className="h-4 w-4" /> Em trânsito
                  </span>
                )}
              </div>
            </div>

            {/* Documento — linhas rótulo ··· valor */}
            <Grupo titulo="Documento">
              <Linha label="Número" value={xml.numero} num />
              <Linha label="Emissão" value={formatDateTime(xml.dataHoraEmissao)} num />
              {xml.empresa != null && <Linha label="Empresa" value={xml.empresa} num />}
            </Grupo>

            {/* Emitente — bloco de nome + dados */}
            <Grupo titulo="Emitente">
              <div className="px-3 py-2.5">
                <p className="text-sm font-medium text-tx" title={xml.emitenteNome ?? undefined}>{xml.emitenteNome || 'Não informado'}</p>
                {temFantasia && <p className="mt-0.5 text-[13px] text-tx-3">{xml.emitenteFantasia}</p>}
              </div>
              <div className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="text-[13px] text-tx-3">CNPJ</span>
                <span className="flex items-center gap-2">
                  <span className="font-mono text-sm font-medium tabular-nums text-tx">{cnpjFormatado}</span>
                  <button
                    onClick={() => handleCopy(cnpjFormatado, 'cnpj')}
                    className="flex h-6 w-6 items-center justify-center rounded-iris-sm text-tx-3 transition-colors hover:bg-surface hover:text-tx"
                    title="Copiar CNPJ"
                  >
                    {copiedKey === 'cnpj' ? <CheckIcon className="h-3.5 w-3.5 text-ok" /> : <DocumentDuplicateIcon className="h-3.5 w-3.5" />}
                  </button>
                </span>
              </div>
              {xml.emitenteIe && <Linha label="Inscrição estadual" value={xml.emitenteIe} num />}
            </Grupo>

            {/* Chave de acesso */}
            {xml.id && (
              <div>
                <div className="mb-1.5 flex items-center justify-between px-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-tx-3">Chave de acesso</p>
                  <button
                    onClick={() => handleCopy(xml.id!, 'chave')}
                    className="inline-flex items-center gap-1.5 rounded-iris-sm px-1.5 py-0.5 text-xs font-medium text-accent-text transition-opacity hover:opacity-80"
                  >
                    {copiedKey === 'chave' ? <><CheckIcon className="h-3.5 w-3.5" /> Copiada</> : <><DocumentDuplicateIcon className="h-3.5 w-3.5" /> Copiar</>}
                  </button>
                </div>
                <p className="break-all rounded-iris border border-line bg-surface-2 p-3 font-mono text-[13px] text-tx-2">{xml.id}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 border-t border-line bg-surface-2 px-4 py-3">
            <button onClick={onClose} className={btnSecundario}>Fechar</button>
            <button onClick={handleBaixarXml} disabled={baixarXmlMutation.isPending} className={btnSecundario}>
              <DocumentTextIcon className="h-4 w-4" />
              {baixarXmlMutation.isPending ? 'Preparando…' : 'XML'}
            </button>
            <button onClick={handleBaixarDanfe} disabled={baixarDanfeMutation.isPending} className={btnPrimario}>
              <ArrowDownTrayIcon className="h-4 w-4" />
              {baixarDanfeMutation.isPending ? 'Gerando…' : 'DANFE'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Grupo rotulado com lista de linhas (borda + divisores). */
const Grupo: React.FC<{ titulo: string; children: React.ReactNode }> = ({ titulo, children }) => (
  <div>
    <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wide text-tx-3">{titulo}</p>
    <div className="divide-y divide-line rounded-iris border border-line">{children}</div>
  </div>
);

/** Linha rótulo ··· valor (à direita). */
const Linha: React.FC<{ label: string; value: React.ReactNode; num?: boolean }> = ({ label, value, num }) => (
  <div className="flex items-center justify-between gap-3 px-3 py-2">
    <span className="text-[13px] text-tx-3">{label}</span>
    <span className={`text-right text-sm font-medium text-tx ${num ? 'tabular-nums' : ''}`}>{value}</span>
  </div>
);

export default XmlTerceirosModal;
